import type {
  ContinueTextRequest,
  CritiqueTextRequest,
  DistillTextRequest,
  ExpandTextRequest,
  GenerateTextRequest,
  RefineTextRequest,
  RestructureTextRequest,
  StylizeTextRequest,
  TextSelectionTarget,
} from '../../../domain';
import type { StudioParams } from '../../shared/panels/swatchSelection';
import { mergePromptWithFragments } from '../../shared/panels/swatchSelection';

/**
 * Pure builders: form state → typed text-operation request DTO. Mirrors Image
 * Lab's buildRequests — validation throws so panels branch on a typed error.
 *
 * v3.0 SCOPE MODEL: all generative ops operate whole-document (`scope:
 * 'Document'`) — the FE replaces the document with the result's `outputText`.
 * This assumes the BE returns the full updated document (the response contract
 * isn't yet typed in the swagger — gap #1). Verify against a live response; if
 * the BE returns deltas, narrow the per-op scope here. Selection-scoped editing
 * is a v3.0.1 enhancement (paired with SwatchInjectionPlugin).
 */

const trimToUndefined = (value: string | undefined): string | undefined => {
  const t = value?.trim();
  return t ? t : undefined;
};

const requireDocument = (documentId: string | null, op: string): string => {
  if (!documentId) throw new Error(`${op} needs an existing draft — generate one first.`);
  return documentId;
};

// ---------- form states + defaults ----------

export interface GenerateTextFormState {
  prompt: string;
  /** 0 = let the BE choose. */
  targetWordCount: number;
}
export const DEFAULT_GENERATE_TEXT_STATE: GenerateTextFormState = { prompt: '', targetWordCount: 0 };

export interface ContinueTextFormState {
  targetWordCount: number;
}
export const DEFAULT_CONTINUE_TEXT_STATE: ContinueTextFormState = { targetWordCount: 200 };

export interface ExpandTextFormState {
  expansionFactor: number;
  direction: string;
}
export const DEFAULT_EXPAND_TEXT_STATE: ExpandTextFormState = { expansionFactor: 1.5, direction: '' };

export interface RefineTextFormState {
  direction: string;
}
export const DEFAULT_REFINE_TEXT_STATE: RefineTextFormState = { direction: '' };

export interface RestructureTextFormState {
  structureHint: string;
}
export const DEFAULT_RESTRUCTURE_TEXT_STATE: RestructureTextFormState = { structureHint: '' };

export interface StylizeTextFormState {
  targetStyle: string;
}
export const DEFAULT_STYLIZE_TEXT_STATE: StylizeTextFormState = { targetStyle: '' };

export interface CritiqueTextFormState {
  /** Comma-separated aspects, e.g. "pacing, dialogue, clarity". */
  aspects: string;
}
export const DEFAULT_CRITIQUE_TEXT_STATE: CritiqueTextFormState = { aspects: '' };

export interface DistillTextFormState {
  targetWordCount: number;
  placement: TextSelectionTarget;
}
export const DEFAULT_DISTILL_TEXT_STATE: DistillTextFormState = {
  targetWordCount: 150,
  placement: 'ReplaceDocument',
};

// ---------- builders ----------

/**
 * Storyteller threads sectionId on every op (Gap 6 — BE updates the section's
 * CurrentDraftId / Status to follow the latest draft). On builders for ops
 * other than Generate, sectionId is the only non-form-state arg, so we keep
 * the signature minimal: state, sessionId, documentId, sectionId.
 */
export function buildGenerateTextRequest(
  state: GenerateTextFormState,
  sessionId: string | undefined,
  documentId: string | null,
  studioParams?: StudioParams,
  referenceImageId?: string,
  sectionId?: string,
): GenerateTextRequest {
  const fragments = studioParams?.promptFragments ?? [];
  const prompt = mergePromptWithFragments(state.prompt, fragments);
  if (!prompt) throw new Error('A prompt is required to generate.');
  return {
    sessionId,
    documentId: documentId ?? undefined,
    scope: 'Document',
    prompt,
    targetWordCount: state.targetWordCount > 0 ? state.targetWordCount : undefined,
    referenceImageId: trimToUndefined(referenceImageId),
    sectionId: trimToUndefined(sectionId),
  };
}

export function buildContinueTextRequest(
  state: ContinueTextFormState,
  sessionId: string | undefined,
  documentId: string | null,
  sectionId?: string,
): ContinueTextRequest {
  return {
    sessionId,
    documentId: requireDocument(documentId, 'Continue'),
    scope: 'Document',
    targetWordCount: state.targetWordCount,
    cursorOffset: 0,
    sectionId: trimToUndefined(sectionId),
  };
}

export function buildExpandTextRequest(
  state: ExpandTextFormState,
  sessionId: string | undefined,
  documentId: string | null,
  sectionId?: string,
): ExpandTextRequest {
  if (state.expansionFactor < 1.2 || state.expansionFactor > 5) {
    throw new Error('Expansion factor must be between 1.2 and 5.');
  }
  return {
    sessionId,
    documentId: requireDocument(documentId, 'Expand'),
    scope: 'Document',
    expansionFactor: state.expansionFactor,
    direction: trimToUndefined(state.direction),
    sectionId: trimToUndefined(sectionId),
  };
}

export function buildRefineTextRequest(
  state: RefineTextFormState,
  sessionId: string | undefined,
  documentId: string | null,
  sectionId?: string,
): RefineTextRequest {
  return {
    sessionId,
    documentId: requireDocument(documentId, 'Refine'),
    scope: 'Document',
    direction: trimToUndefined(state.direction),
    sectionId: trimToUndefined(sectionId),
  };
}

export function buildRestructureTextRequest(
  state: RestructureTextFormState,
  sessionId: string | undefined,
  documentId: string | null,
  sectionId?: string,
): RestructureTextRequest {
  return {
    sessionId,
    documentId: requireDocument(documentId, 'Restructure'),
    scope: 'Document',
    structureHint: trimToUndefined(state.structureHint),
    sectionId: trimToUndefined(sectionId),
  };
}

export function buildStylizeTextRequest(
  state: StylizeTextFormState,
  sessionId: string | undefined,
  documentId: string | null,
  sectionId?: string,
): StylizeTextRequest {
  const targetStyle = state.targetStyle.trim();
  if (!targetStyle) throw new Error('Pick a voice/tone (target style) to stylize.');
  return {
    sessionId,
    documentId: requireDocument(documentId, 'Stylize'),
    scope: 'Document',
    targetStyle,
    sectionId: trimToUndefined(sectionId),
  };
}

export function buildCritiqueTextRequest(
  state: CritiqueTextFormState,
  sessionId: string | undefined,
  documentId: string | null,
  sectionId?: string,
): CritiqueTextRequest {
  const aspects = state.aspects
    .split(',')
    .map(a => a.trim())
    .filter(Boolean);
  return {
    sessionId,
    documentId: requireDocument(documentId, 'Critique'),
    scope: 'Document',
    aspects: aspects.length > 0 ? aspects : undefined,
    sectionId: trimToUndefined(sectionId),
  };
}

export function buildDistillTextRequest(
  state: DistillTextFormState,
  sessionId: string | undefined,
  documentId: string | null,
  sectionId?: string,
): DistillTextRequest {
  if (state.targetWordCount < 50 || state.targetWordCount > 2000) {
    throw new Error('Target word count must be between 50 and 2000.');
  }
  return {
    sessionId,
    documentId: requireDocument(documentId, 'Distill'),
    scope: 'Document',
    targetWordCount: state.targetWordCount,
    placement: state.placement,
    sectionId: trimToUndefined(sectionId),
  };
}
